import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AgentBuilderService } from '../services/agent-builder.service';
import { MarketplaceController } from "../../../../../shared/marketplace-controller/marketplace-controller";
import {
  UsersGroupsControllerComponent
} from "../../../../../shared/users-groups-controller/users-groups-controller.component";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '../../../../../services/api.service';
import { ConversationTypeEnum } from '../../../../../pages/conversation-starter/conversation-type-enum';
import {
  PendingAccessUsersDialogComponent
} from '../../../../../shared/pending-access-users-dialog/pending-access-users-dialog.component';
import { INotifiedUser } from '../../../../../interfaces/i-notification';
import { IUser } from '../../../../../interfaces/i-user-profile';

@Component({
  selector: 'agent-security',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MarketplaceController,
    UsersGroupsControllerComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './agent-security.component.html',
  styleUrl: './agent-security.component.scss'
})
export class AgentSecurityComponent {
  agentForm!: FormGroup;

  /** Users who have requested access and are awaiting approval. */
  readonly pendingUsers = signal<INotifiedUser[]>([]);
  /** True while the pending-users API call is in flight. */
  readonly pendingUsersLoading = signal(false);

  #formBuilder = inject(FormBuilder);
  #agentBuilderService = inject(AgentBuilderService);
  #apiService = inject(ApiService);
  #dialog = inject(MatDialog);

  /** Track the last agent id we fetched pending users for to avoid duplicate calls. */
  #lastFetchedId = '';

  constructor() {

    this.agentForm = this.#formBuilder.group({
      owners: new FormControl([], [Validators.required]),
      users: new FormControl([]),
      groups: new FormControl([]),
      isAdvertised: new FormControl(false),
      isAutoApproved: new FormControl(false),
      notifyAutoApproval: new FormControl(false)
    });

    // Initialize owners once from the current agent snapshot if the parent form
    // currently has no owners. Do this once so the child UsersGroupsController
    // can pick up initial owners, but we won't overwrite user changes later.
    const snapshotAgent = this.#agentBuilderService.agent();
    const snapshotOwners = snapshotAgent?.details?.owners ?? [];
    const currentOwnersNow = this.agentForm.get('owners')?.value ?? [];
    if ((!currentOwnersNow || currentOwnersNow.length === 0) && snapshotOwners.length > 0) {
      this.agentForm.get('owners')?.setValue(snapshotOwners, { emitEvent: false });
    }

    // Keep a single FormGroup instance and patch values when the agent signal changes.
    // Also fires the pending-users call once the agent id becomes available, handling
    // both stepper navigation and direct deep-link bookmarks.
    effect(() => {
      const agent = this.#agentBuilderService.agent();
      // Defensive: ensure agentForm exists (it was created above)
      if (!this.agentForm) return;

      // Patch users, groups, and marketplace flags to keep them in sync with the agent signal.
      // We deliberately do NOT touch the 'owners' control here so that the child
      // UsersGroupsController remains authoritative for owner add/remove actions.
      this.agentForm.patchValue({
        users: agent?.details?.users ?? [],
        groups: agent?.details?.groups ?? [],
        isAdvertised: agent?.details?.isAdvertised ?? false,
        isAutoApproved: agent?.details?.isAutoApproved ?? false,
        notifyAutoApproval: agent?.details?.notifyAutoApproval ?? false
      }, { emitEvent: false });

      // Ensure marketplace controller reacts when the API reports isAdvertised=true.
      // Because patchValue above uses emitEvent:false to avoid overwriting child-controlled
      // fields (owners), we re-emit the isAdvertised value so MarketplaceController's
      // subscriptions pick up the change and update showMarketplaceSettings.
      const advControl = this.agentForm.get('isAdvertised');
      if (advControl) {
        advControl.setValue(advControl.value, { emitEvent: true });
      }

      const agentId = agent?.id;
      if (agentId && agentId !== this.#lastFetchedId) {
        this.#lastFetchedId = agentId;
        // Seed owners from the newly-loaded agent (edit/clone scenario).
        const owners = agent?.details?.owners ?? [];
        if (owners.length > 0) {
          this.agentForm.get('owners')?.setValue(owners, { emitEvent: false });
        }
        this.pendingUsersLoading.set(true);
        this.#apiService.getPendingUsers(agentId, ConversationTypeEnum.Agent).subscribe({
          next: (users: INotifiedUser[]) => this.pendingUsers.set(users ?? []),
          error: () => this.pendingUsers.set([]),
          complete: () => this.pendingUsersLoading.set(false)
        });
      }
    });
  }

  /**
   * Opens the pending access users dialog.
   * Any users confirmed in the dialog are merged into the `users` form control.
   */
  openPendingUsersDialog(): void {
    const dialogRef = this.#dialog.open(PendingAccessUsersDialogComponent, {
      data: { pendingUsers: this.pendingUsers() }
    });

    dialogRef.afterClosed().subscribe((selectedUsers: IUser[] | undefined) => {
      if (!selectedUsers?.length) return;
      const current: IUser[] = this.agentForm.get('users')?.value ?? [];
      const merged = [
        ...current,
        ...selectedUsers.filter(s => !current.some(c => c.userId === s.userId))
      ];
      this.agentForm.get('users')?.setValue(merged);
      this.updateAgent();
    });
  }

  /**
   * Handler for the ownersChanged event emitted by the UsersGroupsControllerComponent.
   * Called whenever the owners control value changes in the child component.
   * Updates the workflow state with the latest security form values.
   */
  onOwnersChanged() {
    this.updateAgent();
  }

  /**
   * Updates the workflow service with the current values from the security form.
   * Synchronizes owners, users, and groups with the workflow state.
   */
  updateAgent() {
    // Use the signal `update` method so the update is applied atomically
    // and will react correctly if multiple pieces of code try to update
    // the agent concurrently. This also avoids relying on a snapshot
    // of the current value which could be stale.
    this.#agentBuilderService.agent.update(currentAgent => ({
      ...currentAgent,
      details: {
        ...currentAgent.details,
        ...this.agentForm.value
      }
    }));
  }

  /**
   * Marks all controls in the agent form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the AgentBuilderComponent.
   */
  markFormAsTouched(): void {
    this.agentForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.agentForm) {
      return false;
    }

    // Reset form errors
    this.agentForm.get('owners')?.setErrors(null);

    this.agentForm.markAllAsTouched();

    // Validate owners field is required
    if (!this.agentForm.get('owners')?.value || this.agentForm.get('owners')?.value.length === 0) {
      this.agentForm.get('owners')?.setErrors({ required: true });
    }

    return this.agentForm.valid;
  }
}
